import { LikeOutlined, ScheduleOutlined } from "@ant-design/icons";
import { Button, Card, Flex, Statistic } from "antd";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { PieChart, Pie, Cell, ResponsiveContainer } from "recharts";
import useAccess from "../../Hooks/useAccess";
import jobDescriptionService from "../../Service/job.description.service";
import cartService from "../../Service/cart.service";
import { useAppContext } from "../../Context/AppContext/AppContext";
import AuthService from "../../Service/auth.service";

function StatsList() {
  const navigate = useNavigate();
  const { currentSubscription, currentPlan, currentLimits , currentMode } = useAccess();
  const { addons } = useAppContext();
  const [statistic, setStatistic] = useState<any>();
  const user = AuthService.getInstance().getCurrentUser();

  // Contrasting color combinations for charts
  const pastelColors = {
    jobPosts: ["#FF6B6B", "#4ECDC4"], // Bright red vs teal
    applications: ["#45B7D1", "#96CEB4"], // Blue vs mint green
    shortlisted: ["#FFEAA7", "#DDA0DD"], // Yellow vs plum
    hired: ["#FF8A80", "#81C784"], // Coral vs green
  };

  const fetchStats = async () => {
    try {
      const data = await jobDescriptionService.getStatisticsCounts();
      setStatistic(data);
    } catch (error) {
      console.error("🚀 ~ fetchStats ~ error:", error);
    }
  };

  const handleAddMoreClick = (type: "jobPost" | "jobApplication") => {
    let addonPlan;
    if (type === "jobPost") {
      addonPlan = addons.find((p) => p.name === "Job posts Add-on");
    } else {
      addonPlan = addons.find((p) => p.name === "Assessment Add-on");
    }

    if (addonPlan) {
      cartService.clearCart();
      navigate(`/plan-purchase?addonId=${addonPlan?._id}&action=addon`);
    }
  };

  // Chart data functions using actual available data
  const getJobPostsChartData = () => {
    const used = currentSubscription?.measurements?.jobPosts || 0;
    const total = currentLimits?.jobPosts || 0;

    if (total === 0) {
      return [{ name: "Used", value: used, fill: pastelColors.jobPosts[0] }];
    }

    const remaining = Math.max(0, total - used);
    return [
      { name: "Used", value: used, fill: pastelColors.jobPosts[0] },
      { name: "Remaining", value: remaining, fill: pastelColors.jobPosts[1] },
    ];
  };

  const getApplicationsChartData = () => {
    const used = currentSubscription?.measurements?.applicationsProcessed || 0;
    const total = currentLimits?.applicationsProcessed || 0;

    if (total === 0) {
      return [
        { name: "Used", value: used, fill: pastelColors.applications[0] },
      ];
    }

    const remaining = Math.max(0, total - used);
    return [
      { name: "Used", value: used, fill: pastelColors.applications[0] },
      {
        name: "Remaining",
        value: remaining,
        fill: pastelColors.applications[1],
      },
    ];
  };

  const getShortlistedChartData = () => {
    const shortlisted = statistic?.noOfShortListedApplications || 0;
    const total = statistic?.noOfApplications || 0;

    if (total === 0) {
      return [
        {
          name: "Shortlisted",
          value: shortlisted,
          fill: pastelColors.shortlisted[0],
        },
      ];
    }

    const others = Math.max(0, total - shortlisted);
    return [
      {
        name: "Shortlisted",
        value: shortlisted,
        fill: pastelColors.shortlisted[0],
      },
      { name: "Others", value: others, fill: pastelColors.shortlisted[1] },
    ];
  };

  const getHiredChartData = () => {
    const hired = statistic?.noOfHiredApplications || 0;
    const total = statistic?.noOfApplications || 0;

    if (total === 0) {
      return [{ name: "Hired", value: hired, fill: pastelColors.hired[0] }];
    }

    const others = Math.max(0, total - hired);
    return [
      { name: "Hired", value: hired, fill: pastelColors.hired[0] },
      { name: "Others", value: others, fill: pastelColors.hired[1] },
    ];
  };

  useEffect(() => {
    fetchStats();
  }, []);

const isHideAddonCTA = currentMode && currentMode !== "base";

  return (
    <div className="stats-list">
      <Card size="small" bordered hoverable className="stat-card">
        <Flex justify="space-between" align="flex-start">
          <div style={{ flex: 1 }}>
            <Statistic
              title="Job Posts"
              value={currentSubscription ? currentSubscription?.measurements?.jobPosts : "0"}
              suffix={currentLimits && currentLimits.jobPosts > 0 && `/ ${currentLimits ? currentLimits?.jobPosts : "0"}`}
              valueStyle={{
                color:
                  currentSubscription?.measurements?.jobPosts === currentLimits?.jobPosts
                    ? "#cf1322"
                    : "#3f8600",
              }}
            />
            {currentPlan &&
              !isHideAddonCTA &&
              user &&
              user?.role === "admin" && (
                <Button
                  type="link"
                  size="small"
                  onClick={() => handleAddMoreClick("jobPost")}
                >
                  Add More
                </Button>
              )}
          </div>
          <div style={{ width: 80, height: 80 }}>
            {currentSubscription && currentLimits && (
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={getJobPostsChartData()}
                    cx="50%"
                    cy="50%"
                    innerRadius={24}
                    outerRadius={32}
                    paddingAngle={0}
                    dataKey="value"
                  >
                    {getJobPostsChartData().map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.fill} />
                    ))}
                  </Pie>
                </PieChart>
              </ResponsiveContainer>
            )}
          </div>
        </Flex>
      </Card>

      <Card size="small" bordered hoverable className="stat-card">
        <Flex justify="space-between" align="flex-start">
          <div style={{ flex: 1 }}>
            <Statistic
              title="Assessments"
              value={
                currentSubscription
                  ? currentSubscription?.measurements?.applicationsProcessed
                  : "0"
              }
              suffix={`/ ${currentLimits ? currentLimits?.applicationsProcessed : "0"}`}
              valueStyle={{
                color:
                  currentSubscription?.measurements?.applicationsProcessed ===
                  currentLimits?.applicationsProcessed
                    ? "#cf1322"
                    : "#3f8600",
              }}
            />
{currentPlan && !isHideAddonCTA && user && user?.role === "admin" && 
              !isHideAddonCTA &&
              user &&
              user?.role === "admin" && (
                <Button
                  type="link"
                  size="small"
                  onClick={() => handleAddMoreClick("jobApplication")}
                >
                  Add More
                </Button>
              )}
          </div>
          <div style={{ width: 80, height: 80 }}>
            {currentSubscription && currentLimits && (
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={getApplicationsChartData()}
                    cx="50%"
                    cy="50%"
                    innerRadius={24}
                    outerRadius={32}
                    paddingAngle={0}
                    dataKey="value"
                  >
                    {getApplicationsChartData().map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.fill} />
                    ))}
                  </Pie>
                </PieChart>
              </ResponsiveContainer>
            )}
          </div>
        </Flex>
      </Card>

      <Card size="small" bordered hoverable className="stat-card">
        <Flex justify="space-between" align="flex-start">
          <div style={{ flex: 1 }}>
            <Statistic
              title="Shortlisted"
              value={statistic ? statistic?.noOfShortListedApplications : "0"}
              suffix={`/ ${statistic ? statistic?.noOfApplications : "0"}`}
              prefix={<ScheduleOutlined />}
            />
          </div>
          <div style={{ width: 80, height: 80 }}>
            {statistic && (
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={getShortlistedChartData()}
                    cx="50%"
                    cy="50%"
                    innerRadius={24}
                    outerRadius={32}
                    paddingAngle={0}
                    dataKey="value"
                  >
                    {getShortlistedChartData().map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.fill} />
                    ))}
                  </Pie>
                </PieChart>
              </ResponsiveContainer>
            )}
          </div>
        </Flex>
      </Card>

      <Card size="small" bordered hoverable className="stat-card">
        <Flex justify="space-between" align="flex-start">
          <div style={{ flex: 1 }}>
            <Statistic
              title="Hired"
              value={statistic ? statistic?.noOfHiredApplications : "0"}
              suffix={`/ ${statistic ? statistic?.noOfApplications : "0"}`}
              prefix={<LikeOutlined />}
            />
          </div>
          <div style={{ width: 80, height: 80 }}>
            {statistic && (
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={getHiredChartData()}
                    cx="50%"
                    cy="50%"
                    innerRadius={24}
                    outerRadius={32}
                    paddingAngle={0}
                    dataKey="value"
                  >
                    {getHiredChartData().map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.fill} />
                    ))}
                  </Pie>
                </PieChart>
              </ResponsiveContainer>
            )}
          </div>
        </Flex>
      </Card>
    </div>
  );
}

export default StatsList;
